/**
 * <search-suggest> — 検索窓(snippets/search-tabbed.liquid)の振る舞い。
 *
 * - 入力欄のフォーカス / クリックで「おすすめキーワード」パネルを開き、
 *   外側のクリック・要素外へのフォーカス移動・Esc で閉じる
 * - ドロワー内でも使うため click / mousedown / touchstart / focusout の伝播を止め、
 *   MenuDrawer(assets/global.js)がドロワーを閉じないようにする
 *   (旧 search-tabbed.liquid のインライン script から移設)
 * - Esc は keydown でパネルを閉じ、直後の keyup の伝播を止める
 *   (MenuDrawer は keyup の Escape でドロワーを閉じるため、1 回目はパネルのみ、2 回目でドロワーが閉じる)
 * - StickyHeader(sections/header-custom-5icons.liquid)が isOpen を参照し、パネル表示中はスクロール処理を止める
 */
class SearchSuggest extends HTMLElement {
  constructor() {
    super();
    this.stopPropagation = (event) => event.stopPropagation();
    this.onInputFocus = this.onInputFocus.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onPanelMouseDown = this.onPanelMouseDown.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.suppressEscapeKeyUp = false;
  }

  get isOpen() {
    return Boolean(this.panel) && !this.panel.hidden;
  }

  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.panel = this.querySelector('[data-search-suggest-panel]');

    this.addEventListener('click', this.stopPropagation);
    this.addEventListener('mousedown', this.stopPropagation);
    this.addEventListener('touchstart', this.stopPropagation, { passive: true });
    this.addEventListener('focusout', this.onFocusOut);

    if (!this.input || !this.panel) return;

    this.input.addEventListener('focus', this.onInputFocus);
    this.input.addEventListener('click', this.onInputFocus);
    this.panel.addEventListener('mousedown', this.onPanelMouseDown);
    this.addEventListener('keydown', this.onKeyDown);
    this.addEventListener('keyup', this.onKeyUp);
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  open() {
    if (!this.panel || this.isOpen) return;
    this.panel.hidden = false;
    this.classList.add('search-suggest--open');
  }

  close() {
    if (!this.panel || !this.isOpen) return;
    this.panel.hidden = true;
    this.classList.remove('search-suggest--open');
  }

  getLinks() {
    return this.panel ? Array.from(this.panel.querySelectorAll('a')) : [];
  }

  onInputFocus() {
    this.open();
  }

  onFocusOut(event) {
    // MenuDrawer の focusout 処理へ渡さない(旧実装の踏襲)
    event.stopPropagation();
    if (!this.panel) return;
    // 要素外へフォーカスが移ったときだけ閉じる(パネル内リンクへの Tab 移動では閉じない)
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onDocumentClick(event) {
    // 要素内のクリックは stopPropagation で document に届かないため、ここに来るのは外側のクリックだけ
    if (!this.isOpen || this.contains(event.target)) return;
    this.close();
  }

  onPanelMouseDown(event) {
    // キーワードを押したときに入力欄のフォーカスを奪わない
    // (Safari で focusout → 閉じる → クリックが成立しない、を防ぐ)
    if (event.target.closest('a')) event.preventDefault();
  }

  onKeyDown(event) {
    if (event.key === 'Escape' || event.code === 'Escape') {
      // 閉じているときは何もしない(2 回目の Esc は MenuDrawer に渡り、ドロワーが閉じる)
      if (!this.isOpen) return;
      event.preventDefault();
      this.input.focus();
      this.close();
      this.suppressEscapeKeyUp = true;
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    if (!this.isOpen) return;
    const links = this.getLinks();
    if (links.length === 0) return;

    const current = links.indexOf(document.activeElement);
    event.preventDefault();
    if (event.key === 'ArrowDown') {
      const next = current < 0 ? 0 : Math.min(current + 1, links.length - 1);
      links[next].focus();
      return;
    }
    if (current <= 0) {
      this.input.focus();
      return;
    }
    links[current - 1].focus();
  }

  onKeyUp(event) {
    if (!this.suppressEscapeKeyUp) return;
    if (event.key === 'Escape' || event.code === 'Escape') {
      this.suppressEscapeKeyUp = false;
      event.stopPropagation();
    }
  }
}

if (!customElements.get('search-suggest')) {
  customElements.define('search-suggest', SearchSuggest);
}
